import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Tables } from '@/integrations/supabase/types';
import { toast } from '@/components/ui/use-toast';

type Project = Tables<'projects'>;
type ProjectStaff = Tables<'project_staff'>;

export interface ProjectWithDetails extends Project {
  projectStaff?: ProjectStaff[];
  pmStaff?: Tables<'staff'>;
  tasks?: Tables<'staff_todos'>[];
}

export const useProjects = (): {
  projects: ProjectWithDetails[];
  isLoading: boolean;
  error: any;
  createProject: (data: { project: Omit<Project, 'id' | 'created_at' | 'updated_at'>; staffIds: string[] }) => void;
  updateProject: (data: { project: Project; staffIds: string[] }) => void;
  deleteProject: (id: string) => void;
} => {
  const queryClient = useQueryClient();

  // Fetch all projects with related data
  const { data: projects = [], isLoading, error, refetch } = useQuery({
    queryKey: ['projects'],
    queryFn: async () => {
      // Full query
      const { data: projectsData, error: projectsError } = await supabase
        .from('projects')
        .select('*')
        .order('created_at', { ascending: false });

      if (projectsError) {
        console.error('Error fetching projects:', projectsError);
        throw projectsError;
      }

      // Then, get project staff relationships
      const { data: projectStaffData, error: projectStaffError } = await supabase
        .from('project_staff')
        .select('*');

      if (projectStaffError) throw projectStaffError;

      // Get staff details for PM
      const { data: staffData, error: staffError } = await supabase
        .from('staff')
        .select('*');
      
      if (staffError) throw staffError;

      // Finally, get tasks for each project
      const { data: tasksData, error: tasksError } = await supabase
        .from('staff_todos')
        .select('*')
        .not('project_id', 'is', null);

      if (tasksError) throw tasksError;

      // Combine the data
      const projectsWithDetails: ProjectWithDetails[] = (projectsData || []).map(project => {
        const projectStaff = (projectStaffData || []).filter(ps => ps.project_id === project.id);
        const tasks = (tasksData || []).filter(task => task.project_id === project.id);
        const pmStaff = (staffData || []).find(s => s.id === project.pm_staff_id);

        return {
          ...project,
          projectStaff,
          tasks,
          pmStaff,
        };
      });

      return projectsWithDetails;
    },
  });

  // Create a new project
  const createProject = useMutation({
    mutationFn: async ({ 
      project, 
      staffIds 
    }: { 
      project: Omit<Project, 'id' | 'created_at' | 'updated_at'>; 
      staffIds: string[] 
    }) => {
      // Create the project
      const { data: newProject, error: projectError } = await supabase
        .from('projects')
        .insert(project)
        .select()
        .single();

      if (projectError) throw projectError;
      

      // Add staff members to the project
      if (staffIds.length > 0) {
        const projectStaffData = staffIds.map(staffId => ({
          project_id: newProject.id,
          staff_id: staffId,
          role: staffId === project.pm_staff_id ? 'pm' : 'member',
        }));

        const { error: staffError } = await supabase
          .from('project_staff')
          .insert(projectStaffData);

        if (staffError) throw staffError;
      }

      return newProject;
    },
    onSuccess: async (data) => {
      // Invalidate and wait for refetch
      await queryClient.invalidateQueries({ queryKey: ['projects'] });
      
      toast({
        title: 'Success',
        description: 'Project created successfully',
      });
      
      // Force immediate refetch
      await queryClient.refetchQueries({ queryKey: ['projects'] });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: 'Failed to create project',
        variant: 'destructive',
      });
      console.error('Error creating project:', error);
    },
  });

  // Update a project
  const updateProject = useMutation({
    mutationFn: async ({ 
      project, 
      staffIds 
    }: { 
      project: Project; 
      staffIds: string[] 
    }) => {
      // Update the project
      const { error: projectError } = await supabase
        .from('projects')
        .update({
          name: project.name,
          description: project.description,
          clinic_id: project.clinic_id,
          pm_staff_id: project.pm_staff_id,
          status: project.status,
          start_date: project.start_date,
          end_date: project.end_date,
          updated_at: new Date().toISOString(),
        })
        .eq('id', project.id);

      if (projectError) throw projectError;

      // Update staff members
      // First, delete existing staff relationships
      const { error: deleteError } = await supabase
        .from('project_staff')
        .delete()
        .eq('project_id', project.id);

      if (deleteError) throw deleteError;

      // Then, add new staff relationships
      if (staffIds.length > 0) {
        const projectStaffData = staffIds.map(staffId => ({
          project_id: project.id,
          staff_id: staffId,
          role: staffId === project.pm_staff_id ? 'pm' : 'member',
        }));

        const { error: staffError } = await supabase
          .from('project_staff')
          .insert(projectStaffData);

        if (staffError) throw staffError;
      }

      return project;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast({
        title: 'Success',
        description: 'Project updated successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: 'Failed to update project',
        variant: 'destructive',
      });
      console.error('Error updating project:', error);
    },
  });

  // Delete a project
  const deleteProject = useMutation({
    mutationFn: async (projectId: string) => {
      const { error } = await supabase
        .from('projects')
        .delete()
        .eq('id', projectId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast({
        title: 'Success',
        description: 'Project deleted successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: 'Failed to delete project',
        variant: 'destructive',
      });
      console.error('Error deleting project:', error);
    },
  });

  return {
    projects,
    isLoading,
    error,
    createProject: createProject.mutate,
    updateProject: updateProject.mutate,
    deleteProject: deleteProject.mutate,
  };
};